import { Component, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

// ----------------------------------------------------------------------

const STORAGE_KEY = 'chunk-reload-retry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary that catches chunk/lazy-load failures
 * (e.g. "Unexpected token '<'" when server returns HTML instead of JS).
 * On first failure it auto-reloads; on repeated failure it shows a retry button.
 */
export default class LazyLoadErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk') ||
      error.message?.includes("Unexpected token '<'") ||
      error.message?.includes('Failed to fetch dynamically imported module');

    if (isChunkError) {
      const lastRetry = sessionStorage.getItem(STORAGE_KEY);
      const now = Date.now();

      // Auto-reload once if we haven't retried in the last 10 seconds
      if (!lastRetry || now - Number(lastRetry) > 10_000) {
        sessionStorage.setItem(STORAGE_KEY, String(now));
        window.location.reload();
        return;
      }
    }
  }

  handleRetry = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
            gap: 2,
            p: 3,
          }}
        >
          <Typography variant="h6">La page n'a pas pu être chargée</Typography>
          <Typography variant="body2" color="text.secondary">
            Une nouvelle version est peut-être disponible.
          </Typography>
          <Button variant="contained" onClick={this.handleRetry}>
            Actualiser la page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
